import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAdminUsers } from "@/constants/roleNav";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PlusCircle, Pencil, UserX, UserCheck, Trash2, KeyRound, Copy, Mail, Loader2, ShieldAlert,
} from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const allowed = canAdminUsers(user?.role);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roles, setRoles] = useState({ suntronix_roles: [], customer_roles: [] });
  const [masters, setMasters] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [pwdDialog, setPwdDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);

  const load = useCallback(() => {
    if (!allowed) return;
    api.get("/admin/users", { params: { limit: 100, q: q || undefined } }).then(({ data }) => setRows(data.items || []));
  }, [allowed, q]);
  useEffect(() => {
    if (!allowed) return undefined;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, allowed, load]);

  useEffect(() => {
    if (!allowed) return;
    Promise.all([api.get("/admin/roles"), api.get("/master-companies")]).then(([r, m]) => {
      setRoles(r.data);
      setMasters(m.data.items || []);
    });
  }, [allowed]);

  if (!allowed) return <Navigate to="/" replace />;

  const vaultCheck = async (u) => {
    try {
      const { data } = await api.get("/vault/ownership", { params: { email: u.email } });
      const n = data.summary?.owned + data.summary?.backup;
      if (n > 0) toast.warning(`${u.email} owns ${data.summary.owned} Secure Vault credential(s) (+${data.summary.backup} as backup) — transfer ownership, rotate shared secrets and revoke access.`,
        { duration: 12000, action: { label: "Open Secure Vault", onClick: () => window.location.assign("/admin/security/vault?tab=audit") } });
    } catch { /* vault not accessible for this role */ }
  };

  const archive = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deactivated"); load(); vaultCheck(u); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "User set to Inactive" : "User set to Active");
      load();
      if (u.active) vaultCheck(u);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hardDelete = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`, { params: { hard: true } });
      toast.success("User permanently deleted"); setHardConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const roleOptions = [
    ...(roles.suntronix_roles || []).map((r) => ({ value: r, label: `Suntronix · ${r.replace(/_/g, " ")}` })),
    ...(roles.customer_roles || []).map((r) => ({ value: r, label: `Customer · ${r.replace(/customer_/g, "").replace(/_/g, " ")}` })),
  ];

  const isCreate = !dialog.initial;
  const fields = [
    { name: "email", label: "Email", type: "email", required: isCreate, help: isCreate ? undefined : "Cannot be changed after creation" },
    { name: "name", label: "Full name", required: true },
    ...(isCreate ? [{ name: "password", label: "Initial password", type: "text", required: true, help: "User can rotate on first login" }] : []),
    { name: "role", label: "Role", type: "select", required: true, options: roleOptions },
    { name: "master_company_id", label: "Master company", type: "select", options: [{ value: "", label: "— (Suntronix internal)" }, ...masters.map((m) => ({ value: m.id, label: m.name }))] },
  ];

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Administration</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Users & Roles</h1>
        </div>
        <Button className="bg-slate-900" onClick={() => setDialog({ open: true, initial: null })} data-testid="user-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add user
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3">
        <Input placeholder="Search email or name…" className="h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5">Email</th>
              <th className="text-left px-3 py-2.5">Name</th>
              <th className="text-left px-3 py-2.5">Role</th>
              <th className="text-left px-3 py-2.5">Master Company</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2 capitalize text-slate-700">{(u.role || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{u.master_company_id ? u.master_company_id.slice(0, 8) : "—"}</td>
                <td className="px-3 py-2"><StatusBadge value={u.active ? "active" : "inactive"} tone={u.active ? "green" : "slate"} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit" onClick={() => setDialog({ open: true, initial: u })} data-testid={`user-edit-${u.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Reset password" onClick={() => setPwdDialog({ open: true, initial: u })} data-testid={`user-pwd-${u.id}`}>
                    <KeyRound className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className={`h-7 px-2 ${u.active ? "text-amber-600" : "text-emerald-600"}`} title={u.active ? "Set Inactive" : "Set Active"} onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>
                    {u.active ? <><UserX className="h-3.5 w-3.5 mr-1" />Deactivate</> : <><UserCheck className="h-3.5 w-3.5 mr-1" />Activate</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" title="Delete permanently" onClick={() => setHardConfirm(u)} data-testid={`user-delete-${u.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CrudDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        title={dialog.initial ? `Edit ${dialog.initial.email}` : "Add user"}
        fields={fields}
        initial={dialog.initial}
        endpoint={dialog.initial ? `/admin/users/${dialog.initial.id}` : "/admin/users"}
        method={dialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="user"
      />
      <TempPasswordDialog
        user={pwdDialog.initial}
        open={pwdDialog.open}
        onOpenChange={(o) => setPwdDialog({ ...pwdDialog, open: o })}
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Deactivate ${confirm?.email}?`}
        description="User will be marked inactive and unable to sign in."
        onConfirm={() => archive(confirm)}
        testId="user-confirm"
      />
      <ConfirmDeleteDialog
        open={!!hardConfirm}
        onOpenChange={() => setHardConfirm(null)}
        title={`Permanently delete ${hardConfirm?.email}?`}
        description="This removes the user account completely and cannot be undone. To keep history, use Deactivate instead."
        confirmLabel="Delete permanently"
        onConfirm={() => hardDelete(hardConfirm)}
        testId="user-hard-confirm"
      />
    </div>
  );
}

/**
 * Reset password dialog with 3 clear paths:
 *   1. Generate a strong temp password (auto-copied, shown once, forces
 *      the user to change on next login).
 *   2. Set a specific password manually (advanced admins only).
 *   3. Email a self-service set-password link (never see plaintext).
 */
function TempPasswordDialog({ user, open, onOpenChange }) {
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);      // { temp_password }
  const [manual, setManual] = useState("");
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    if (!open) { setIssued(null); setManual(""); setEmailed(false); }
  }, [open]);

  if (!user) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/temp-password`);
      setIssued(data);
      // Immediately copy so it's on the clipboard before the admin closes the modal.
      try { await navigator.clipboard.writeText(data.temp_password); } catch { /* clipboard blocked */ }
      toast.success("Temp password generated and copied to clipboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setSpecific = async () => {
    if (!manual.trim() || manual.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { password: manual });
      toast.success(`Password set for ${user.email}`);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const emailLink = async () => {
    setBusy(true);
    try {
      await api.post(`/auth/password-reset/request`, { email: user.email });
      setEmailed(true);
      toast.success(`Set-password link issued for ${user.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyPw = () => {
    navigator.clipboard.writeText(issued.temp_password);
    toast.success("Password copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password · {user.email}</DialogTitle>
        </DialogHeader>

        {!issued && !emailed && (
          <div className="space-y-4">
            <div className="rounded border border-slate-200 p-3 space-y-2">
              <div className="flex items-center gap-2 text-[13px] font-medium text-slate-900">
                <KeyRound className="h-3.5 w-3.5" /> Generate a strong temp password (recommended)
              </div>
              <p className="text-[12px] text-slate-500 leading-relaxed">
                We create a 12-char password like <span className="font-mono">9apM-8HnS-Yjym</span>,
                set it on the account, and show it here once. The user will
                be forced to change it on next login.
              </p>
              <Button onClick={generate} disabled={busy} className="bg-slate-900 h-8" data-testid="temp-pw-generate">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
                Generate & show
              </Button>
            </div>

            <div className="rounded border border-slate-200 p-3 space-y-2">
              <div className="text-[13px] font-medium text-slate-900">Email a set-password link</div>
              <p className="text-[12px] text-slate-500">
                We email the user a one-time link (30-min expiry). You never see the password.
              </p>
              <Button onClick={emailLink} disabled={busy} variant="outline" className="h-8" data-testid="temp-pw-email">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email link
              </Button>
            </div>

            <details className="rounded border border-slate-200 p-3">
              <summary className="text-[13px] font-medium text-slate-900 cursor-pointer">Set a specific password</summary>
              <div className="mt-2 space-y-2">
                <Input type="text" value={manual} onChange={(e) => setManual(e.target.value)}
                  placeholder="At least 8 characters" data-testid="temp-pw-manual-input" />
                <Button onClick={setSpecific} disabled={busy || manual.length < 8} className="h-8" data-testid="temp-pw-manual-save">
                  Save this password
                </Button>
              </div>
            </details>
          </div>
        )}

        {issued && (
          <div className="space-y-3" data-testid="temp-pw-result">
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-800 mb-1">Temp password (shown once)</div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-[16px] text-slate-900 tracking-wide select-all">
                  {issued.temp_password}
                </div>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={copyPw} data-testid="temp-pw-copy">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="rounded border border-amber-200 bg-amber-50 p-2.5 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-amber-900 leading-relaxed">
                Share via a secure channel. The user must change it on next login.
                We won&apos;t show this password again — close the dialog when done.
              </div>
            </div>
          </div>
        )}

        {emailed && (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900">
            Set-password link issued to <b>{user.email}</b>. Link expires in 30 min.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="temp-pw-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
